import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import InfinityMark from '../components/InfinityMark';
import { useT } from '../lib/i18n';

const PHASES = ['opening', 'observation', 'interpretation', 'feeling', 'friction', 'connection', 'action', 'closing'];

const QUICK_OPENING_KEYS = [
  { key: 'argument',  i18n: 'opening.quick.argument' },
  { key: 'anxiety',   i18n: 'opening.quick.anxiety' },
  { key: 'shame',     i18n: 'opening.quick.shame' },
  { key: 'overwhelm', i18n: 'opening.quick.overwhelm' },
];

const FEELING_KEYS = [
  { key: 'tightness-in-chest', i18n: 'feeling.chip.tightness-in-chest' },
  { key: 'tension-in-stomach', i18n: 'feeling.chip.tension-in-stomach' },
  { key: 'restlessness',       i18n: 'feeling.chip.restlessness' },
  { key: 'anger',              i18n: 'feeling.chip.anger' },
  { key: 'shame',              i18n: 'feeling.chip.shame' },
  { key: 'numbness',           i18n: 'feeling.chip.numbness' },
];

export default function SessionFlow({ onExit }) {
  const navigate = useNavigate();
  const { t, lang } = useT();
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [data, setData] = useState({
    opening: '', observation: '', interpretation: '', feeling: '', connection: '', action: '',
  });
  const [obsAnalysis, setObsAnalysis] = useState({ flagged_words: [], suggestion: '' });
  const [actions, setActions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [frictionSeconds] = useState(() => {
    const v = parseInt(localStorage.getItem('ctl_friction_seconds') || '8', 10);
    return Math.max(3, Math.min(20, v));
  });
  const [frictionRemaining, setFrictionRemaining] = useState(frictionSeconds);

  useEffect(() => {
    api.post('/sessions').then(r => setSessionId(r.data.id)).catch(() => {});
  }, []);

  const phase = PHASES[phaseIdx];

  useEffect(() => {
    if (phase !== 'friction') return;
    setFrictionRemaining(frictionSeconds);
    const tmr = setInterval(() => {
      setFrictionRemaining((r) => {
        if (r <= 1) {
          clearInterval(tmr);
          setTimeout(() => setPhaseIdx((i) => i + 1), 600);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(tmr);
    // eslint-disable-next-line
  }, [phase]);

  const saveEntry = async (type, content) => {
    if (!sessionId || !content) return;
    try { await api.post(`/sessions/${sessionId}/entries`, { type, content }); } catch (_) {}
  };

  const fallbackActions = () => [
    t('action.fallback.0'), t('action.fallback.1'), t('action.fallback.2'), t('action.fallback.3'),
  ];

  const goNext = async () => {
    if (phase === 'opening') {
      await saveEntry('opening', data.opening);
    } else if (phase === 'observation') {
      await saveEntry('observation', data.observation);
    } else if (phase === 'interpretation') {
      await saveEntry('interpretation', data.interpretation);
    } else if (phase === 'feeling') {
      await saveEntry('feeling', data.feeling);
      setAiLoading(true);
      api.post('/ai/actions', {
        observation: data.observation,
        interpretation: data.interpretation,
        feeling: data.feeling,
        language: lang,
      }).then(r => setActions(r.data.suggestions || []))
        .catch(() => setActions(fallbackActions()))
        .finally(() => setAiLoading(false));
    } else if (phase === 'connection') {
      await saveEntry('connection', data.connection);
    } else if (phase === 'action') {
      await saveEntry('action', data.action);
    } else if (phase === 'closing') {
      try { if (sessionId) await api.post(`/sessions/${sessionId}/close`); } catch (_) {}
      onExit();
      return;
    }
    setPhaseIdx((i) => i + 1);
  };

  const analyzeObservation = async () => {
    if (!data.observation.trim()) return;
    setAiLoading(true);
    try {
      const r = await api.post('/ai/observation', { text: data.observation, language: lang });
      setObsAnalysis(r.data);
    } catch (_) {
      setObsAnalysis({ flagged_words: [], suggestion: '' });
    } finally {
      setAiLoading(false);
    }
  };

  const phaseProgress = `${phaseIdx + 1} ${t('session.progress')}`;

  return (
    <div className="min-h-screen flex flex-col fade-in" data-testid={`phase-${phase}`} key={phase}>
      <header className="ctl-container w-full pt-8 flex items-center justify-between">
        <div className="ctl-eyebrow" data-testid="session-progress">
          {phase !== 'friction' && phase !== 'closing' ? phaseProgress : ''}
        </div>
        {phase !== 'friction' && (
          <button onClick={onExit} className="ctl-btn-quiet" data-testid="session-exit-btn">
            {t('session.leave')}
          </button>
        )}
      </header>

      <main className="flex-1 flex items-center justify-center py-16">
        <div className="ctl-container w-full" key={phase}>
          {phase === 'opening' && (
            <div className="space-y-10 fade-in">
              <h1 className="ctl-h1" data-testid="opening-headline">{t('opening.headline')}</h1>
              <textarea
                className="ctl-textarea"
                placeholder={t('opening.placeholder')}
                value={data.opening}
                onChange={(e) => setData({ ...data, opening: e.target.value })}
                data-testid="opening-input"
                autoFocus
              />
              <div>
                <div className="ctl-eyebrow mb-3">{t('opening.orPick')}</div>
                <div className="flex flex-wrap gap-3">
                  {QUICK_OPENING_KEYS.map(({ key, i18n }) => {
                    const label = t(i18n);
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`ctl-chip ${data.opening === label ? 'ctl-chip-active' : ''}`}
                        onClick={() => setData({ ...data, opening: label })}
                        data-testid={`opening-quick-${key}`}
                      >{label}</button>
                    );
                  })}
                </div>
              </div>
              <div className="pt-4">
                <button className="ctl-btn" onClick={goNext} disabled={!data.opening.trim()} data-testid="opening-next-btn">
                  {t('opening.continue')}
                </button>
              </div>
            </div>
          )}

          {phase === 'observation' && (
            <div className="space-y-10 fade-in">
              <div>
                <h1 className="ctl-h1 mb-4" data-testid="observation-headline">{t('obs.headline')}</h1>
                <p className="ctl-helper">{t('obs.helper')}</p>
              </div>
              <textarea
                className="ctl-textarea"
                placeholder={t('obs.placeholder')}
                value={data.observation}
                onChange={(e) => { setData({ ...data, observation: e.target.value }); setObsAnalysis({ flagged_words: [], suggestion: '' }); }}
                onBlur={analyzeObservation}
                data-testid="observation-input"
                autoFocus
              />
              {(obsAnalysis.flagged_words.length > 0 || obsAnalysis.suggestion) && (
                <div className="border-l border-[#968369] pl-4 space-y-3" data-testid="observation-feedback">
                  {obsAnalysis.flagged_words.length > 0 && (
                    <p className="ctl-helper text-sm">
                      {t('obs.flagged')}{' '}
                      {obsAnalysis.flagged_words.map((w, i) => (
                        <span key={i} className="ctl-flagged">{w}</span>
                      )).reduce((acc, el, i) => i === 0 ? [el] : [...acc, ', ', el], [])}
                    </p>
                  )}
                  {obsAnalysis.suggestion && (
                    <p className="ctl-helper text-sm" data-testid="observation-suggestion">
                      {obsAnalysis.suggestion}
                    </p>
                  )}
                </div>
              )}
              <div className="pt-2 flex items-center gap-4">
                <button className="ctl-btn" onClick={goNext} disabled={!data.observation.trim()} data-testid="observation-next-btn">
                  {t('obs.continue')}
                </button>
                {aiLoading && <span className="ctl-helper text-xs slow-pulse">{t('obs.reading')}</span>}
              </div>
            </div>
          )}

          {phase === 'interpretation' && (
            <div className="space-y-10 fade-in">
              <div>
                <h1 className="ctl-h1 mb-4" data-testid="interpretation-headline">{t('interp.headline')}</h1>
                <p className="ctl-helper">{t('interp.helper')}</p>
              </div>
              <textarea
                className="ctl-textarea"
                placeholder={t('interp.placeholder')}
                value={data.interpretation}
                onChange={(e) => setData({ ...data, interpretation: e.target.value })}
                data-testid="interpretation-input"
                autoFocus
              />
              <div className="pt-2">
                <button className="ctl-btn" onClick={goNext} disabled={!data.interpretation.trim()} data-testid="interpretation-next-btn">
                  {t('interp.continue')}
                </button>
              </div>
            </div>
          )}

          {phase === 'feeling' && (
            <div className="space-y-10 fade-in">
              <div>
                <h1 className="ctl-h1 mb-4" data-testid="feeling-headline">{t('feeling.headline')}</h1>
                <p className="ctl-helper">{t('feeling.helper')}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                {FEELING_KEYS.map(({ key, i18n }) => {
                  const label = t(i18n);
                  const selected = data.feeling.split(', ').includes(label);
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`ctl-chip ${selected ? 'ctl-chip-active' : ''}`}
                      onClick={() => {
                        const cur = data.feeling.split(', ').filter(Boolean);
                        const next = selected ? cur.filter(x => x !== label) : [...cur, label];
                        setData({ ...data, feeling: next.join(', ') });
                      }}
                      data-testid={`feeling-chip-${key}`}
                    >{label}</button>
                  );
                })}
              </div>
              <textarea
                className="ctl-textarea"
                placeholder={t('feeling.placeholder')}
                value={!FEELING_KEYS.some(({ i18n }) => data.feeling.includes(t(i18n))) ? data.feeling : ''}
                onChange={(e) => setData({ ...data, feeling: e.target.value })}
                data-testid="feeling-free-input"
              />
              <div className="pt-2">
                <button className="ctl-btn" onClick={goNext} disabled={!data.feeling.trim()} data-testid="feeling-next-btn">
                  {t('feeling.continue')}
                </button>
              </div>
            </div>
          )}

          {phase === 'friction' && (
            <div className="min-h-[70vh] flex items-center justify-center fade-in" data-testid="friction-screen">
              <div className="text-center">
                <p className="font-display text-3xl md:text-4xl font-light text-[#1C1B1A]" data-testid="friction-headline">
                  {t('friction.headline')}
                </p>
                <p className="ctl-helper mt-8 text-sm slow-pulse" data-testid="friction-subtext">
                  {t('friction.subtext')}
                </p>
                <span className="sr-only" data-testid="friction-remaining">{frictionRemaining}</span>
              </div>
            </div>
          )}

          {phase === 'connection' && (
            <div className="space-y-12 fade-in">
              <h1 className="ctl-h1" data-testid="connection-headline">{t('connection.headline')}</h1>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  className="ctl-btn"
                  onClick={() => { setData({ ...data, connection: t('connection.yes') }); setTimeout(goNext, 100); }}
                  data-testid="connection-yes-btn"
                >
                  {t('connection.yes')}
                </button>
                <button
                  className="ctl-btn"
                  onClick={() => { setData({ ...data, connection: t('connection.no') }); setTimeout(goNext, 100); }}
                  data-testid="connection-no-btn"
                >
                  {t('connection.no')}
                </button>
              </div>
            </div>
          )}

          {phase === 'action' && (
            <div className="space-y-10 fade-in">
              <div>
                <h1 className="ctl-h1 mb-4" data-testid="action-headline">{t('action.headline')}</h1>
                <p className="ctl-helper">{t('action.helper')}</p>
              </div>
              {aiLoading && actions.length === 0 ? (
                <p className="ctl-helper text-sm slow-pulse">{t('action.reading')}</p>
              ) : (
                <div className="flex flex-col gap-3" data-testid="action-suggestions">
                  {actions.map((a, i) => (
                    <button
                      key={a}
                      type="button"
                      className={`ctl-chip text-left justify-start ${data.action === a ? 'ctl-chip-active' : ''}`}
                      onClick={() => setData({ ...data, action: a })}
                      data-testid={`action-suggestion-${i}`}
                    >{a}</button>
                  ))}
                </div>
              )}
              <textarea
                className="ctl-textarea"
                placeholder={t('action.placeholder')}
                value={!actions.includes(data.action) ? data.action : ''}
                onChange={(e) => setData({ ...data, action: e.target.value })}
                data-testid="action-free-input"
              />
              <div className="pt-2">
                <button className="ctl-btn" onClick={goNext} disabled={!data.action.trim()} data-testid="action-done-btn">
                  {t('action.done')}
                </button>
              </div>
            </div>
          )}

          {phase === 'closing' && (
            <div className="text-center fade-in" data-testid="closing-screen">
              <div className="flex justify-center mb-12 opacity-90">
                <InfinityMark size={96} stroke={1.1} />
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-light mb-6" data-testid="closing-headline">
                {t('closing.headline')}
              </h1>
              <p className="ctl-helper mb-16" data-testid="closing-subtext">
                {t('closing.subtext')}
              </p>
              <button className="ctl-btn" onClick={goNext} data-testid="closing-close-btn">
                {t('closing.close')}
              </button>
              <div className="mt-16">
                <button
                  type="button"
                  onClick={() => setPhaseIdx(PHASES.indexOf('connection'))}
                  className="bg-transparent border-0 cursor-pointer text-xs text-[#2A2724] opacity-40 hover:opacity-70 transition-opacity duration-500 italic"
                  style={{ letterSpacing: '0.02em' }}
                  data-testid="closing-notready-btn"
                >
                  {t('closing.notReady')}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
